import React from 'react'
import {
  add,
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  startOfToday,
  startOfWeek,
  subDays,
} from 'date-fns'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'

const toLocalDate = (value) => {
  if (value instanceof Date) return value
  const dateText = String(value || '').slice(0, 10)
  const match = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return new Date(value)
}

const InternalCalendarMenu = ({
  view,
  setView,
  selectedDay,
  setSelectedDay,
  calendarDaysWithEvents,
  setFirstDayOfWeek,
  setLastDayOfWeek,
  setDisplayedWeek,
  lastDayOfWeek,
  firstDayOfWeek,
  firstDayOfCurrentMonth,
  setCurrentMonth,
  currentMonth,
  monthName,
}) => {
  const selectedDate = toLocalDate(selectedDay.date)

  const selectDay = (day) => {
    if (typeof setCurrentMonth === 'function') {
      setCurrentMonth(format(day, 'MMMM yyyy'))
    }
    const formattedDay = format(day, 'yyyy-MM-dd')
    const dayDetails = calendarDaysWithEvents?.[formattedDay] || {}
    setSelectedDay({
      date: formattedDay,
      events: dayDetails.events || [],
      availabilities: dayDetails.availabilities || [],
    })
  }

  const changeWeek = (day) => {
    const weekStart = startOfWeek(day)
    const weekEnd = endOfWeek(day)
    setLastDayOfWeek(weekEnd)
    setFirstDayOfWeek(weekStart)
    setDisplayedWeek(eachDayOfInterval({start: weekStart, end: weekEnd}))
  }

  const changeMonth = (numberOfMonths) => {
    const nextMonth = add(firstDayOfCurrentMonth, { months: numberOfMonths })
    setCurrentMonth(format(nextMonth, 'MMMM yyyy'))
  }

  const flipBack = () => {
    if (view === 'Day view') selectDay(subDays(selectedDate, 1))
    if (view === 'Week view') changeWeek(subDays(new Date(firstDayOfWeek), 1))
    if (view === 'Month view') changeMonth(-1)
  }

  const flipForward = () => {
    if (view === 'Day view') selectDay(addDays(selectedDate, 1))
    if (view === 'Week view') changeWeek(addDays(new Date(lastDayOfWeek), 1))
    if (view === 'Month view') changeMonth(1)
  }

  const selectToday = () => {
    const today = startOfToday()
    if (view === 'Day view') selectDay(today)
    if (view === 'Week view') {
      selectDay(today)
      changeWeek(today)
    }
    if (view === 'Month view') selectDay(today)
  }

  const dateHeading = view === 'Day view'
    ? format(selectedDate, 'MMMM d, yyyy')
    : view === 'Week view'
      ? monthName
      : currentMonth

  return (
    <header className='calendar__internal-menu'>
      <div className='calendar__date-navigation'>
        <button type='button' className='calendar__today-button' onClick={selectToday}>Today</button>
        <button type='button' className='calendar__nav-icon' onClick={flipBack} aria-label={`Previous ${view.replace(' view', '').toLowerCase()}`}>
          <ChevronLeftIcon aria-hidden='true' />
        </button>
        <button type='button' className='calendar__nav-icon' onClick={flipForward} aria-label={`Next ${view.replace(' view', '').toLowerCase()}`}>
          <ChevronRightIcon aria-hidden='true' />
        </button>
        <div className='calendar__date-heading'>
          <strong>{dateHeading}</strong>
          {view === 'Day view' && <span>{format(selectedDate, 'EEEE')}</span>}
        </div>
      </div>

      <label className='calendar__view-picker'>
        <span className='sr-only'>Calendar view</span>
        <select value={view} onChange={(event) => setView(event.target.value)} aria-label='Calendar view'>
          <option value='Day view'>Day</option>
          <option value='Week view'>Week</option>
          <option value='Month view'>Month</option>
        </select>
      </label>
    </header>
  )
}

export default InternalCalendarMenu
